import * as React from 'react';
import { CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import type { PageTourKey } from '@/lib/tours/registry';
import { usePageTourActions } from './PageTourHost';
import { startTourFromPopover } from './startTourFromPopover';

interface DrawerTourHelpProps {
  tourKey: PageTourKey;
  /** Short suggestion shown before starting the drawer tour. */
  suggestion: string;
  buttonLabel?: string;
}

/**
 * Help control for drawer UIs — use for tours that walk through form fields inside a drawer.
 */
export function DrawerTourHelp({
  tourKey,
  suggestion,
  buttonLabel = 'Start tour',
}: DrawerTourHelpProps) {
  const { startPageTour } = usePageTourActions();
  const [open, setOpen] = React.useState(false);

  const handleStartTour = () => {
    startTourFromPopover(startPageTour, tourKey, () => setOpen(false));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0 text-muted-foreground"
          aria-label="Form help and tour"
        >
          <CircleHelp className="h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 p-3 text-sm">
        <p className="text-muted-foreground">{suggestion}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-full"
          onPointerDown={(event) => {
            event.preventDefault();
          }}
          onClick={handleStartTour}
        >
          {buttonLabel}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
